import React, { useState } from 'react';
import DrawOutlinedIcon from '@mui/icons-material/DrawOutlined';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { appDocumentTitle } from '../appMeta';
import { useAuth } from '../auth/AuthContext';
import { DefaultSignatureDialog } from '../components/userSignature/DefaultSignatureDialog';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { PageShell } from '../layout/PageShell';
import {
  useDeleteMyUserSignedMutation,
  useMyUserSignedQuery,
} from '../query/hooks/useMyUserSigned';

export function ProfilePage() {
  useDocumentTitle(appDocumentTitle('My profile'));

  const { user, jwt } = useAuth();
  const userTypeLabel =
    user?.userTypeName?.trim() || user?.userTypeCode?.trim() || 'Not set';
  const signedQ = useMyUserSignedQuery(jwt);
  const deleteM = useDeleteMyUserSignedMutation(jwt);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const previewSrc = signedQ.data?.sign || signedQ.data?.file_url || '';

  async function handleDelete() {
    setDeleteError(null);
    try {
      await deleteM.mutateAsync();
    } catch (e) {
      setDeleteError(e instanceof Error ? e.message : 'Gagal menghapus tanda tangan default.');
    }
  }

  return (
    <PageShell title="My profile" testId="profile-page">
      <Stack spacing={2} sx={{ maxWidth: 560 }}>
        <Paper variant="outlined" sx={{ p: 3 }}>
          <Stack spacing={1.5}>
            <ProfileField label="Username" value={user?.username ?? '—'} />
            <ProfileField label="Email" value={user?.email ?? '—'} />
            <ProfileField label="User type" value={userTypeLabel} />
          </Stack>
        </Paper>

        <Paper variant="outlined" sx={{ p: 3 }} data-testid="profile-default-signature">
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
            Tanda tangan default
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Disimpan sekali, lalu bisa dipakai ulang pada form yang membutuhkan tanda tangan.
          </Typography>
          {deleteError ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              {deleteError}
            </Alert>
          ) : null}
          {signedQ.isLoading ? (
            <Stack alignItems="center" sx={{ py: 2 }}>
              <CircularProgress size={24} />
            </Stack>
          ) : previewSrc ? (
            <Box
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                bgcolor: '#fff',
                height: 140,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                overflow: 'hidden',
                mb: 2,
                p: 1,
              }}
            >
              <Box
                component="img"
                src={previewSrc}
                alt="Tanda tangan default"
                data-testid="profile-default-signature-preview"
                sx={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
              />
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Belum ada tanda tangan default.
            </Typography>
          )}
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Button
              variant="contained"
              startIcon={<DrawOutlinedIcon />}
              onClick={() => {
                setDeleteError(null);
                setDialogOpen(true);
              }}
              data-testid="profile-default-signature-edit"
            >
              {previewSrc ? 'Ubah tanda tangan' : 'Simpan tanda tangan'}
            </Button>
            {previewSrc ? (
              <Button
                color="error"
                disabled={deleteM.isPending}
                onClick={() => void handleDelete()}
                data-testid="profile-default-signature-delete"
              >
                {deleteM.isPending ? 'Menghapus…' : 'Hapus'}
              </Button>
            ) : null}
          </Stack>
        </Paper>
      </Stack>

      <DefaultSignatureDialog open={dialogOpen} onClose={() => setDialogOpen(false)} />
    </PageShell>
  );
}

function ProfileField({ label, value }: { label: string; value: string }) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
        {label}
      </Typography>
      <Typography variant="body1">{value}</Typography>
    </Box>
  );
}
